<template>
    <div class="row gx-3">
        <pc-label
            v-bind:label="label"
            v-bind:for-id="inputId"
            v-bind:show-required="required"
            v-bind:show-help="showHelp"
            v-bind:help-link="showHelpLinkOnly"
            class="col-12"
            v-on:help-clicked="emitHelpClicked">
            <pc-sensitive-icon />
        </pc-label>
        <div class="col-12">
            <slot name="help" />
        </div>
        <pc-input-group v-bind:class="responsiveHelpers.standard">
            <pc-loader
                class="w-100"
                v-bind:loading="retrievingData"
                small>
                <pc-input
                    v-bind="displayInputBinder"
                    v-bind:validation-group-id="inputId"
                    v-on:update:model-value="onDisplayValueInput">
                    <template
                        v-if="showEitherBtn"
                        v-slot:manualAppend="">
                        <button
                            v-if="showViewBtn"
                            v-bind:disabled="forEditor"
                            class="btn border btn-secondary input-group-text"
                            v-on:click="onShowClicked">
                            {{ isExposed ? hideButtonLabel : showButtonLabel }}
                        </button>
                        <button
                            v-if="showEditBtn"
                            v-bind:disabled="forEditor"
                            class="btn border btn-secondary input-group-text"
                            v-on:click="onEditClicked">
                            {{ editButtonLabel }}
                        </button>
                    </template>
                </pc-input>
            </pc-loader>
        </pc-input-group>
        <validation-render-helper
            v-bind:validation-error="validationError"
            v-bind:validation-group-id="inputId" />
    </div>
</template>
<script>
    import editSensitiveValueDialog, {
        DIALOG_RESULT as EDIT_SENSITIVE_DIALOG_RESULT,
        RESULT_HOOK as EDIT_SENSITIVE_RESULT_HOOK
    } from "./editSensitiveValueDialog.vue";

    import base, { SUFFIXES, mapGroupIdsAndValues, validationRenderHelper } from "../_elementBase";
    import { PROJECT_FORM_TYPES, AUX_MUTATION } from "Forms/Components/ProjectForm/projectFormStore";
    import { PROJECT_FORM_CONTROLLER_TYPES } from "Forms/Components/ProjectForm/projectFormControllerStore";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/sensitiveData.i18n";

    export default {
        components: {
            "validation-render-helper": validationRenderHelper
        },
        extends: base,
        data() {
            return {
                isExposed: false,
                needsMfaPrompt: !this.$app.loggedInUserHasMfa && !this.$app.loggedInUserIsSsoInProgram,
                forEditor: false, // TODO! When implementing edit form
                hasSavedValue: true,

                displayValue: null,
                retrievingData: false,
                hasPlaintext: false
            };
        },
        computed: {
            displayInputBinder() {
                return {
                    type: this.isExposed ? "text" : "password",
                    autocomplete: this.isExposed ? "" : "new-password",
                    key: this.isExposed ? `${this.inputId}-revealed` : `${this.inputId}-hidden`,
                    id: this.inputId,
                    modelValue: this.displayValue,
                    required: this.required,
                    validator: this.validator,
                    readonly: !this.canEditDirectly,
                    hasManualAppend: this.showViewBtn || this.showEditBtn,
                    disabled: !this.canEditDirectly
                };
            },
            isEmpty() {
                return this.displayValue == null || this.displayValue === "";
            },
            showViewBtn() {
                return (
                    !this.isPrefill &&
                    (this.forEditor ||
                        (!this.hasSavedValue && !this.readonly) ||
                        this.hasPlaintext ||
                        (this.hasSavedValue && this.$app.loggedInUserHasViewSensitiveDataPrivilege))
                );
            },
            showEditBtn() {
                return this.forEditor || (!this.canEditDirectly && !this.$app.loggedInUserIsReadOnly && !this.readonly);
            },
            showEitherBtn() {
                return this.showViewBtn || this.showEditBtn;
            },
            canEditDirectly() {
                return !this.hasSavedValue && !this.readonly;
            },
            readonly() {
                return this.baseReadonly || this.isPrefill;
            },
            ...mapGroupIdsAndValues({
                obfuscated: SUFFIXES.SensitiveData.Obfuscated
            }),
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        // These data values must be set in the created hook rather than in the
        // data declaration because their initial values depend on obfuscated,
        // which is itself a computed.
        created() {
            const isInitiallyEmpty = this.obfuscated == null || this.obfuscated.length == 0;

            if (!this.projectElement) {
                return;
            }

            this.hasSavedValue = Boolean(
                this.projectElement.auxState != null ? this.projectElement.auxState.HasSavedValue : !isInitiallyEmpty
            );
            this.hasPlaintext = Boolean(
                this.projectElement.auxState != null ? this.projectElement.auxState.HasPlaintext : isInitiallyEmpty
            );

            this.displayValue =
                this.projectElement.auxState != null ? this.projectElement.auxState.DisplayValue : this.obfuscated;

            this._commitAuxState();
        },
        methods: {
            async onEditClicked() {
                if (!this.isProjectModuleRegistered()) {
                    return;
                }

                this.isExposed = false;

                const { action, data } = await this.$app.showDialog({
                    title: this.editHeader,
                    customComponent: editSensitiveValueDialog,
                    customComponentOptions: { inputId: this.inputId },
                    customComponentResultHook: EDIT_SENSITIVE_RESULT_HOOK,
                    buttons: [
                        {
                            text: this.cancelButtonLabel,
                            result: EDIT_SENSITIVE_DIALOG_RESULT.cancel
                        },
                        {
                            text: this.okButtonLabel,
                            result: EDIT_SENSITIVE_DIALOG_RESULT.ok
                        }
                    ]
                });

                if (action === EDIT_SENSITIVE_DIALOG_RESULT.cancel) {
                    return;
                }

                this.onEditValueOk(data);
            },
            onEditValueOk(newValue) {
                this.sendDataChange(this.obfuscatedId, newValue);
                this.displayValue = newValue;
                this.hasPlaintext = true;

                // If user emptied the value, treat it as though they reset the element
                if (this.isEmpty) {
                    this.hasSavedValue = false;
                }
                this._commitAuxState();
            },
            async onShowClicked() {
                if (!this.isProjectModuleRegistered()) {
                    return;
                } else if (this.isExposed) {
                    this.isExposed = false;
                    return;
                } else if (this.hasPlaintext) {
                    this.isExposed = true;
                    return;
                } else if (this.needsMfaPrompt) {
                    this.$store.dispatch(PROJECT_FORM_CONTROLLER_TYPES.actions.showMfaDialog, {}, { root: true });
                    return;
                }

                const okResult = "OK";
                const cancelResult = "CANCEL";
                const dialogResult = await this.$app.showDialog({
                    title: this.viewSensitiveDataAlertTitle,
                    message: this.viewSensitiveDataAlertMessage,
                    buttons: [
                        {
                            text: this.cancelButtonLabel,
                            result: cancelResult
                        },
                        {
                            text: this.okButtonLabel,
                            primary: true,
                            result: okResult
                        }
                    ]
                });

                if (dialogResult === okResult) {
                    this.retrievingData = true;
                    const result = await this.$store.dispatch(
                        PROJECT_FORM_TYPES.actions.retrieveSensitiveData,
                        {
                            elementId: this.element.PublicId
                        },
                        { root: true }
                    );
                    if (result.status === "success") {
                        this.displayValue = result.Data.Plaintext;
                    }
                    this.isExposed = true;
                    this.retrievingData = false;
                    this.hasPlaintext = true;
                    this._commitAuxState();
                }
            },
            onDisplayValueInput(value) {
                this.displayValue = value;
                if (!this.readonly && this.canEditDirectly) {
                    this.sendDataChange(this.obfuscatedId, value);
                }
                this._commitAuxState();
            },
            // Commit relevant sensitive data element state to the project form
            // element store so that it can persist across form page changes which
            // cause all form element components to be destroyed and re-created.
            // It's necessary to replace the whole auxState object, so anytime we
            // need to change the value of one state property, we have to include
            // the current values of the other state properties.
            _commitAuxState() {
                // AuxState properties:
                // DisplayValue:
                //  Either the obfuscated value or the plaintext if it was
                //  entered or retrieved since the last page load
                // HasSavedValue:
                //  True if there was a saved value in the sensitive data
                //  element at page load time or if the user emptied the
                //  value since page load
                // HasPlaintext:
                //  True if the user has updated the value since the last
                //  page load or retrieved the plaintext of the stored value.

                const auxState = {
                    DisplayValue: this.displayValue,
                    HasSavedValue: this.hasSavedValue,
                    HasPlaintext: this.hasPlaintext
                };
                this.$store.commit(AUX_MUTATION, {
                    elementId: this.element.PublicId,
                    auxState
                });
            }
        }
    };
</script>
